import { Dispatch, SetStateAction, useCallback } from 'react';
import { ActionMenuItem } from '../../../../components/ActionsMenu/ActionsMenu.types';
import { ExaminingTeamChairmen, ITaskGroup } from '../../../../models/taskGroup.types';
import { ExaminerType, MessageModal } from '../../ExaminerTreemap.types';
import { SubText } from '../../../../components/Prompt/Prompt.component';
import { getIsExaminerBlocked } from '../../Helpers';
import ActionsMenu from '../../../../components/ActionsMenu/ActionsMenu.component';

export type UseExaminingTeamChairmenActionsMenuProps = {
  data: ITaskGroup[];
  setAction: Dispatch<SetStateAction<string>>;
  openAssignModal: (
    title: string,
    type: ExaminerType,
    isMultiChoice?: boolean,
    assessmentId?: string,
    examinerFunctionId?: string,
    secondaryId?: string,
  ) => void;
  setOpenLimitCardPerExaminerDialog: React.Dispatch<React.SetStateAction<boolean>>;
  selectedItemId: React.MutableRefObject<string>;
  selectedTaskGroup: React.MutableRefObject<ITaskGroup | undefined>;
  setPromptSubTexts: React.Dispatch<React.SetStateAction<SubText[] | undefined>>;
  setPromptMainText: React.Dispatch<React.SetStateAction<string>>;
  setIsConfirmationPromptVisible: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenDisableExaminingTeamPrompt: React.Dispatch<React.SetStateAction<boolean>>;
  setIsExaminingTeamChairmen: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenDisableExaminerPrompt: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenEnableExaminerPrompt: React.Dispatch<React.SetStateAction<boolean>>;
  examinerMode?: boolean;
  setMessageModal: React.Dispatch<React.SetStateAction<MessageModal>>;
  setEditExaminerNumberModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export const useExaminingTeamChairmenActionsMenu = ({
  data,
  openAssignModal,
  setAction,
  selectedItemId,
  selectedTaskGroup,
  setOpenLimitCardPerExaminerDialog,
  setIsConfirmationPromptVisible,
  setPromptMainText,
  setPromptSubTexts,
  setIsExaminingTeamChairmen,
  setOpenDisableExaminerPrompt,
  setOpenDisableExaminingTeamPrompt,
  setOpenEnableExaminerPrompt,
  examinerMode,
  setEditExaminerNumberModalOpen,
  setMessageModal,
}: UseExaminingTeamChairmenActionsMenuProps) => {
  const displayRemoveExaminingTeamChairmanAssingmentFunctionPrompt = useCallback(() => {
    const taskGroup = data.find(
      (taskGroup) =>
        taskGroup.examTaskGroupCoordinators.findIndex(
          (examTaskGroupCoordinator) =>
            examTaskGroupCoordinator.examiningTeamChairmen.findIndex(
              (examiningTeamChairmen) => examiningTeamChairmen.teamChairmanId === selectedItemId.current,
            ) !== -1,
        ) !== -1,
    );
    if (taskGroup) {
      selectedTaskGroup.current = taskGroup;
      setPromptMainText('Czy na pewno chcesz usunąć Egzaminatorowi:');
      const chariman = taskGroup.examTaskGroupCoordinators
        .find((coordinator) =>
          coordinator.examiningTeamChairmen.find((chairman) => chairman.teamChairmanId === selectedItemId.current),
        )
        ?.examiningTeamChairmen.find((chairman) => chairman.teamChairmanId == selectedItemId.current)?.examiner;

      const subTexts: SubText[] = [
        { text: `${chariman?.firstName} ${chariman?.lastName}` },
        { text: 'przypisaną funkcję PZE?', isBold: true },
      ];
      setPromptSubTexts(subTexts);
      setIsConfirmationPromptVisible(true);
    }
  }, [data]);

  const displayRemoveExaminingTeamChairmanFunctionPrompt = useCallback(() => {
    const taskGroup = data.find(
      (taskGroup) =>
        taskGroup.examTaskGroupCoordinators.findIndex(
          (examTaskGroupCoordinator) =>
            examTaskGroupCoordinator.examiningTeamChairmen.findIndex(
              (examiningTeamChairmen) => examiningTeamChairmen.teamChairmanId === selectedItemId.current,
            ) !== -1,
        ) !== -1,
    );
    if (taskGroup) {
      selectedTaskGroup.current = taskGroup;
      setPromptMainText('Czy na pewno chcesz usunąć funkcję PZE z:');
      setPromptSubTexts([{ text: taskGroup.name }]);
      setIsConfirmationPromptVisible(true);
    }
  }, [data]);

  const handlePZEAction = useCallback(
    (item: string, assessmentId?: string, examinerFunctionId?: string, examiningTeamId?: string) => {
      setAction(item);

      switch (item) {
        case 'Przypisz egzaminatorowi funkcję PZE':
          openAssignModal('Przypisz egzaminatorowi funkcję PZE', 'PZE', false, assessmentId, examinerFunctionId);
          break;
        case 'Dodaj Egzaminatora/ów do ZE':
          openAssignModal(
            'Dodaj Egzaminatora/Egzaminatorów do ZE:',
            'ZE',
            true,
            assessmentId,
            undefined,
            examiningTeamId,
          );
          break;
        case 'Ustaw limity kart Egzaminatorom':
          setOpenLimitCardPerExaminerDialog(true);
          break;
        case 'Usuń egzaminatorowi funkcję PZE':
          displayRemoveExaminingTeamChairmanAssingmentFunctionPrompt();
          break;
        case 'Przypisz Egzaminatora-Weryfikatora':
          openAssignModal(
            'Przypisz Egzaminatora-Weryfikatora',
            'Egzaminator-Weryfikator',
            false,
            assessmentId,
            undefined,
            examiningTeamId,
          );
          break;
        case 'Usuń funkcję PZE':
          displayRemoveExaminingTeamChairmanFunctionPrompt();
          break;
        case 'Zablokuj ZE':
          setOpenDisableExaminingTeamPrompt(true);
          break;
        case 'Zablokuj PZE':
          {
            setIsExaminingTeamChairmen(true);
          }
          setOpenDisableExaminerPrompt(true);
          break;
        case 'Odblokuj PZE':
          setIsExaminingTeamChairmen(true);
          setOpenEnableExaminerPrompt(true);
          break;
      }
    },
    [
      displayRemoveExaminingTeamChairmanAssingmentFunctionPrompt,
      displayRemoveExaminingTeamChairmanFunctionPrompt,
      openAssignModal,
    ],
  );

  const getExaminingTeamChairmenActionsMenu = useCallback(
    (examiningTeamChairmen: ExaminingTeamChairmen, assessmentId: string, rowIndex: number) => {
      const isQualified = examiningTeamChairmen?.examiner?.isQualified;
      const menuItems: ActionMenuItem[] = [];

      if (examiningTeamChairmen?.examiner?.examinerId) {
        menuItems.push({
          text: 'Usuń egzaminatorowi funkcję PZE',
          onClick: () => {
            handlePZEAction('Usuń egzaminatorowi funkcję PZE');
          },
        });
        menuItems.push({
          text: 'Przypisz Egzaminatora-Weryfikatora',
          onClick: () => {
            handlePZEAction(
              'Przypisz Egzaminatora-Weryfikatora',
              assessmentId,
              examiningTeamChairmen.teamChairmanId,
              examiningTeamChairmen.examiningTeam.examiningTeamId,
            );
          },
        });
        menuItems.push({
          text: 'Dodaj Egzaminatora/ów do ZE',
          onClick: () => {
            handlePZEAction(
              'Dodaj Egzaminatora/ów do ZE',
              assessmentId,
              examiningTeamChairmen.teamChairmanId,
              examiningTeamChairmen.examiningTeam.examiningTeamId,
            );
          },
        });
        menuItems.push({
          text: 'Ustaw limity kart Egzaminatorom',
          onClick: () => {
            handlePZEAction('Ustaw limity kart Egzaminatorom');
          },
        });
        if (examinerMode && !isQualified && !getIsExaminerBlocked(examiningTeamChairmen.examiner, assessmentId)) {
          menuItems.push({
            text: 'Zablokuj ZE',
            onClick: () => {
              handlePZEAction('Zablokuj ZE', assessmentId);
            },
          });
          menuItems.push({
            text: 'Zablokuj PZE',
            onClick: () => {
              handlePZEAction('Zablokuj PZE');
            },
          });
        }
        if (examinerMode && !isQualified && getIsExaminerBlocked(examiningTeamChairmen.examiner, assessmentId)) {
          menuItems.push({
            text: 'Zablokuj ZE',
            onClick: () => {
              handlePZEAction('Zablokuj ZE');
            },
          });
          menuItems.push({
            text: 'Odblokuj PZE',
            onClick: () => {
              handlePZEAction('Odblokuj PZE');
            },
          });
        }
        if (examinerMode && isQualified) {
          menuItems.push({
            text: 'Zablokuj ZE',
            onClick: () => {
              handlePZEAction('Zablokuj ZE');
            },
          });
        }
        menuItems.push({
          text: 'Wyślij wiadomość',
          onClick: () => {
            setAction('Wyślij wiadomość');
            setMessageModal({ open: true, type: 'standard' });
          },
        });
        menuItems.push({
          text: 'Edytuj numer egzaminatora',
          onClick: () => {
            setAction('Edytuj numer egzaminatora');
            setEditExaminerNumberModalOpen(true);
          },
        });
      } else {
        menuItems.push({
          text: 'Przypisz egzaminatorowi funkcję PZE',
          onClick: () => {
            handlePZEAction(
              'Przypisz egzaminatorowi funkcję PZE',
              assessmentId,
              examiningTeamChairmen.teamChairmanId,
              examiningTeamChairmen.examiningTeam.examiningTeamId,
            );
          },
        });
        menuItems.push({
          text: 'Dodaj Egzaminatora/ów do ZE',
          onClick: () => {
            handlePZEAction(
              'Dodaj Egzaminatora/ów do ZE',
              assessmentId,
              examiningTeamChairmen.teamChairmanId,
              examiningTeamChairmen.examiningTeam.examiningTeamId,
            );
          },
        });
        menuItems.push({
          text: 'Usuń funkcję PZE',
          onClick: () => {
            handlePZEAction('Usuń funkcję PZE', assessmentId);
          },
        });
      }
      return <ActionsMenu menuItems={menuItems} rowIndex={rowIndex} />;
    },
    [handlePZEAction, examinerMode],
  );

  return { getExaminingTeamChairmenActionsMenu };
};
